import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import useTokenStore from '../../stores/tokens';
import useModalStore from '../../stores/modal';
import OrganizationSelectModal from '../modal/OrganizationSelectModal';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';

// ─── Icons ──────────────────────────────────────────────────────────────────
const BellIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
        <path d="M10 21a2 2 0 0 0 4 0"/>
    </svg>
);
const LogoutIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
        <path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>
    </svg>
);
const ChevronIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M6 9l6 6 6-6"/>
    </svg>
);
const MenuIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}>
        <path d="M4 7h16M4 12h16M4 17h16"/>
    </svg>
);
const XIcon = (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M18 6L6 18M6 6l12 12"/>
    </svg>
);

// ─── Constants ───────────────────────────────────────────────────────────────
const NAV_BG = "#1c1a2e";
const NAV_BORDER = "#2a2841";

const dropdownPanel = {
    position: "absolute", top: "calc(100% + 4px)", left: 0,
    minWidth: 160,
    background: "#ffffff",
    borderRadius: 10,
    boxShadow: "0 8px 24px -8px rgba(0,0,0,.25)",
    border: "1px solid #f0eff5",
    padding: "6px 0",
    zIndex: 1040,
};

const dropdownItemBase = {
    display: "block",
    padding: "9px 16px",
    fontSize: 13.5,
    color: "#3a3849",
    textDecoration: "none",
    transition: "background 120ms",
    whiteSpace: "nowrap",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    fontFamily: "inherit",
};

// ─── Desktop dropdown item ───────────────────────────────────────────────────
function DropItem({ to, isActive, reloadDocument, children, onClick }) {
    const style = {
        ...dropdownItemBase,
        color: isActive ? PURPLE : "#3a3849",
        fontWeight: isActive ? 600 : 400,
    };
    const handleEnter = (e) => { e.currentTarget.style.background = "#f5f4fa"; };
    const handleLeave = (e) => { e.currentTarget.style.background = "transparent"; };

    if (onClick) {
        return (
            <div style={style} onClick={onClick}
                onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
                {children}
            </div>
        );
    }
    return (
        <Link to={to} reloadDocument={reloadDocument} style={style}
            onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
            {children}
        </Link>
    );
}

// ─── Active underline bar ────────────────────────────────────────────────────
const ActiveBar = () => (
    <span style={{
        position: "absolute", left: "50%", bottom: -1, transform: "translateX(-50%)",
        width: 24, height: 2.5, borderRadius: 2,
        background: `linear-gradient(90deg, ${PURPLE}, ${MINT})`,
    }}/>
);

export default function NavigationBar({ onLogout }) {
    const location = useLocation();
    const { user, setUser, isAuthenticated } = useTokenStore();
    const { isOrgSelectModalOpen, openOrgSelectModal } = useModalStore();
    const isMobile = useIsMobile(1080);

    const [activeDropdown, setActiveDropdown] = React.useState(null);
    const [drawer, setDrawer] = React.useState(false);
    const [expandedMenu, setExpandedMenu] = React.useState(null);
    const hideTimerRef = React.useRef(null);

    // 모바일→데스크톱 전환 시 드로어 닫기
    React.useEffect(() => {
        if (!isMobile) setDrawer(false);
    }, [isMobile]);

    // 드로어 열릴 때 스크롤 잠금
    React.useEffect(() => {
        document.body.style.overflow = drawer ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [drawer]);

    const handleOrgSwitch = async (org) => {
        if (!org || org.organizationCode === user.currentOrganizationCode) return;
        const ok = window.confirm(`현재 조직을 "${org.organizationName}"(으)로 변경하시겠습니까?`);
        if (!ok) return;
        try {
            localStorage.setItem('currentOrganizationCode', org.organizationCode);
            localStorage.setItem('currentOrganizationName', org.organizationName);
            setUser({
                currentOrganizationCode: org.organizationCode,
                currentOrganizationName: org.organizationName,
                currentRole: org.userRole,
                currentOrganizationApproved: org.isApproved,
            });
            window.location.href = '/';
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    };

    const handleMouseEnter = (menuName) => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        setActiveDropdown(menuName);
    };
    const handleMouseLeave = () => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setActiveDropdown(null), 100);
    };

    React.useEffect(() => {
        return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
    }, []);

    const isActive = (path) => location.pathname.toLowerCase().includes(path.toLowerCase());

    const navBtnStyle = (active) => ({
        background: "transparent", border: "none",
        padding: "8px 14px",
        color: active ? "white" : "rgba(255,255,255,.6)",
        fontSize: 13.5, fontWeight: active ? 600 : 500,
        cursor: "pointer", fontFamily: "inherit",
        position: "relative", transition: "color 120ms",
        whiteSpace: "nowrap",
    });

    const NavDropdown = ({ label, isActiveCheck, menuName, children }) => {
        const isOpen = activeDropdown === menuName;
        return (
            <div style={{ position: "relative" }}
                 onMouseEnter={() => handleMouseEnter(menuName)}
                 onMouseLeave={handleMouseLeave}>
                {/* 헤더 네비게이션바 예외 추가 */}
                {menuName === 'init-info' ? (
                    <span>{label}</span>
                ):(
                    <button style={{ ...navBtnStyle(isActiveCheck), display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {label}
                        <ChevronIcon
                            width={13} height={13}
                            style={{
                                color: isActiveCheck ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.4)",
                                transition: "transform 160ms",
                                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                flexShrink: 0,
                            }}
                        />
                        {isActiveCheck && <ActiveBar/>}
                    </button>
                )}
                {isOpen && <div style={dropdownPanel}>{children}</div>}
            </div>
        );
    };

    const logoSvg = (
        <img src="/workitalk_icon_square.png" alt="WorkiTalk" width={32} height={32}
             style={{ display: "block", flexShrink: 0, borderRadius: "22%" }}/>
    );

    // ── 모바일 드로어 내부 메뉴 항목 ──────────────────────────────────────────
    const isAdmin = user?.currentRole === "ADMIN" || user?.isAdmin;

    const drawerMenuItems = isAdmin ? [
        {
            label: "조직", menuName: "org", sub: [
                { label: "조직 관리", to: "/admin/pendingAppr" },
            ]
        },
        {
            label: "회원", menuName: "member", sub: [
                { label: "회원 관리", to: "/admin/readUserList" },
            ]
        },
        {
            label: "푸시", menuName: "push", sub: [
                { label: "푸시 목록", to: "/admin/push/list", reload: true },
                { label: "푸시 생성", to: "/admin/push/create", reload: true },
                { label: "템플릿 목록", to: "/admin/push/template/list", reload: true },
                { label: "템플릿 생성", to: "/admin/push/template/create", reload: true },
            ]
        },
        {
            label: "설정", menuName: "settings", sub: [
                { label: "어드민 설정", to: "/admin/settings", reload: true },
            ]
        },
    ] : [
        // 웹 nav와 동일한 구조: 조직(항상) → 전자결재 → 일정 → 프로젝트 → 보고서 → 푸시 → 노트 (승인 시)
        {
            label: "조직", menuName: "org", sub: [
                { label: "조직 정보", to: "/organization/info" },
                // 회원 목록은 조직정보 화면의 탭으로 이동 — 헤더 중복 제거
                { label: "내 정보 관리", to: "/my-info/check-pw" },
            ]
        },
        ...(user?.currentOrganizationApproved ? [
            {
                label: "전자결재", menuName: "approval", sub: [
                    { label: "기안 작성", to: "/approval/write" },
                    { label: "기안함", to: "/approval/draft-box" },
                    { label: "결재함", to: "/approval/inbox" },
                ]
            },
            { label: "일정", menuName: "calendar", to: "/calendar", reload: true },
            { label: "프로젝트", menuName: "project", to: "/project/list", reload: true },
            {
                label: "보고서", menuName: "report", sub: [
                    { label: "보고서 작성", to: "/report/create" },
                    { label: "보고서 목록", to: "/report" },
                ]
            },
            {
                label: "푸시", menuName: "push", sub: [
                    { label: "푸시 목록", to: "/push/list", reload: true },
                    { label: "푸시 생성", to: "/push/create", reload: true },
                    { label: "템플릿 목록", to: "/push/template/list", reload: true },
                    { label: "템플릿 생성", to: "/push/template/create", reload: true },
                ]
            },
            {
                label: "노트", menuName: "note", sub: [
                    { label: "새 노트 작성", to: "/note/create" },
                    { label: "노트 목록", to: "/note" },
                ]
            },
        ] : []),
        // 내 기본 정보 관리 (웹 로그아웃 옆 드롭다운 대응 - 페이지 구현 후 to 경로 교체 예정)
        { label: "내 기본 정보 관리", menuName: "my-basic-info", to: "/my-info/check-pw" },
    ];

    // ── 모바일 헤더 + 드로어 ──────────────────────────────────────────────────
    if (isMobile) {
        return (
            <>
                <header style={{ background: NAV_BG, color: "white", borderBottom: `1px solid ${NAV_BORDER}`, position: "sticky", top: 0, zIndex: 50 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 56, padding: "0 16px" }}>
                        {/* 로고 */}
                        <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 9 }}>
                            {logoSvg}
                            <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em", color: "white" }}>WorkiTalk</span>
                        </Link>

                        {isAuthenticated && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {/* 알림 벨 */}
                                <button style={{
                                    background: "transparent", border: "1px solid rgba(255,255,255,.15)",
                                    width: 40, height: 40, borderRadius: 10,
                                    color: "rgba(255,255,255,.75)", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <BellIcon width={18} height={18}/>
                                </button>
                                {/* 햄버거 메뉴 */}
                                <button
                                    aria-label="메뉴 열기"
                                    onClick={() => setDrawer(true)}
                                    style={{
                                        background: "transparent", border: "1px solid rgba(255,255,255,.15)",
                                        width: 40, height: 40, borderRadius: 10,
                                        color: "white", cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                    <MenuIcon width={20} height={20}/>
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                {/* 오버레이 + 드로어 */}
                {drawer && (
                    <div
                        onClick={() => setDrawer(false)}
                        style={{
                            position: "fixed", inset: 0, zIndex: 1060,
                            background: "rgba(15,12,30,.55)", backdropFilter: "blur(2px)",
                        }}>
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                position: "absolute", top: 0, right: 0, bottom: 0,
                                width: "min(86vw, 340px)",
                                background: NAV_BG, color: "white",
                                display: "flex", flexDirection: "column",
                                boxShadow: "-16px 0 50px rgba(0,0,0,.45)",
                                overflowY: "auto",
                            }}>

                            {/* 드로어 헤더: 사용자 정보 + 닫기 */}
                            <div style={{
                                display: "flex", alignItems: "center", gap: 11,
                                padding: "16px 16px 14px", borderBottom: `1px solid ${NAV_BORDER}`,
                                flexShrink: 0,
                            }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: "50%",
                                    background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 14, fontWeight: 700, color: "white", flexShrink: 0,
                                }}>
                                    {(user?.userName || '').slice(0, 1)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                                    <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {user?.currentOrganizationName}
                                        {user?.currentRole && <span style={{ marginLeft: 5, opacity: 0.7 }}>{user.currentRole}</span>}
                                    </div>
                                    <div style={{ fontSize: 14, fontWeight: 600 }}>{user?.userName}님</div>
                                </div>
                                <button
                                    aria-label="닫기"
                                    onClick={() => setDrawer(false)}
                                    style={{
                                        background: "transparent", border: "none", padding: 8,
                                        cursor: "pointer", color: "rgba(255,255,255,.6)",
                                        display: "flex", borderRadius: 8, flexShrink: 0,
                                    }}>
                                    <XIcon width={20} height={20}/>
                                </button>
                            </div>

                            {/* 조직 선택 (어드민 아닐 때) */}
                            {!isAdmin && user?.orgUserList?.length > 1 && (
                                <div style={{ padding: "10px 12px", borderBottom: `1px solid ${NAV_BORDER}` }}>
                                    <div style={{ fontSize: 10.5, fontWeight: 600, color: "rgba(255,255,255,.4)", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>조직 전환</div>
                                    {user.orgUserList.map((org) => (
                                        <button
                                            key={org.organizationCode}
                                            onClick={() => { handleOrgSwitch(org); setDrawer(false); }}
                                            style={{
                                                width: "100%", textAlign: "left",
                                                background: org.organizationCode === user.currentOrganizationCode ? "rgba(255,255,255,.08)" : "transparent",
                                                border: "none", borderRadius: 8,
                                                padding: "10px 12px",
                                                color: "rgba(255,255,255,.85)",
                                                fontSize: 13.5, fontWeight: 500,
                                                fontFamily: "inherit", cursor: "pointer",
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                            }}>
                                            <span>{org.organizationName}</span>
                                            {org.organizationCode === user.currentOrganizationCode && (
                                                <span style={{ fontSize: 10, background: hexToRgba(MINT, 0.2), color: MINT, padding: "2px 7px", borderRadius: 999, fontWeight: 600 }}>현재</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* 내비게이션 메뉴 */}
                            <nav style={{ flex: 1, padding: "8px 8px 12px" }}>
                                {drawerMenuItems.map((item) => {
                                    if (item.to) {
                                        // 단순 링크 (일정, 마이페이지 등)
                                        const active = isActive(item.to);
                                        return (
                                            <Link
                                                key={item.menuName}
                                                to={item.to}
                                                reloadDocument={item.reload}
                                                onClick={() => setDrawer(false)}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 9,
                                                    padding: "13px 14px", borderRadius: 10,
                                                    background: active ? "rgba(255,255,255,.09)" : "transparent",
                                                    color: active ? "white" : "rgba(255,255,255,.8)",
                                                    fontSize: 15, fontWeight: active ? 600 : 500,
                                                    textDecoration: "none",
                                                }}>
                                                {active && <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg, ${PURPLE}, ${MINT})`, flexShrink: 0 }}/>}
                                                {item.label}
                                            </Link>
                                        );
                                    }

                                    // 서브메뉴 있는 항목
                                    const isOpen = expandedMenu === item.menuName;
                                    const anySubActive = item.sub?.some(s => isActive(s.to));
                                    return (
                                        <div key={item.menuName}>
                                            <button
                                                onClick={() => setExpandedMenu(isOpen ? null : item.menuName)}
                                                style={{
                                                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                                                    background: anySubActive ? "rgba(255,255,255,.09)" : "transparent",
                                                    border: "none",
                                                    color: anySubActive ? "white" : "rgba(255,255,255,.8)",
                                                    padding: "13px 14px", borderRadius: 10,
                                                    fontSize: 15, fontWeight: anySubActive ? 600 : 500,
                                                    fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                                                }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
                                                    {anySubActive && <span style={{ width: 4, height: 16, borderRadius: 2, background: `linear-gradient(180deg, ${PURPLE}, ${MINT})`, flexShrink: 0 }}/>}
                                                    {item.label}
                                                </span>
                                                <svg width="13" height="13" viewBox="0 0 12 12" fill="none"
                                                    style={{ opacity: .7, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 160ms", flexShrink: 0 }}>
                                                    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            {isOpen && (
                                                <div style={{ padding: "2px 0 6px 12px" }}>
                                                    {item.sub.map((s) => (
                                                        <Link
                                                            key={s.to}
                                                            to={s.to}
                                                            reloadDocument={s.reload}
                                                            onClick={() => setDrawer(false)}
                                                            style={{
                                                                display: "block",
                                                                padding: "11px 14px", borderRadius: 8,
                                                                color: isActive(s.to) ? MINT : "rgba(255,255,255,.62)",
                                                                fontSize: 13.5, fontWeight: isActive(s.to) ? 600 : 500,
                                                                textDecoration: "none",
                                                            }}>
                                                            {s.label}
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </nav>

                            {/* 로그아웃 */}
                            <div style={{ padding: "12px 16px 20px", borderTop: `1px solid ${NAV_BORDER}`, flexShrink: 0 }}>
                                <button
                                    onClick={() => { setDrawer(false); onLogout && onLogout(); }}
                                    style={{
                                        width: "100%", height: 46, borderRadius: 11,
                                        background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.14)",
                                        color: "rgba(255,255,255,.85)", fontSize: 14, fontWeight: 600,
                                        cursor: "pointer", fontFamily: "inherit",
                                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                                    }}>
                                    <LogoutIcon width={16} height={16}/> 로그아웃
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {isOrgSelectModalOpen && <OrganizationSelectModal/>}
            </>
        );
    }

    // ── 데스크톱 헤더 (기존 디자인 유지) ─────────────────────────────────────
    return (
        <>
            <header style={{ background: NAV_BG, color: "white", borderBottom: `1px solid ${NAV_BORDER}` }}>
                <div style={{
                    maxWidth: 1600, margin: "0 auto",
                    display: "flex", alignItems: "center",
                    padding: "0 clamp(20px, 3vw, 38px)", height: 60, gap: 28,
                }}>
                    {/* 로고 */}
                    <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        {logoSvg}
                        <span style={{ fontWeight: 700, fontSize: 16, letterSpacing: "-0.01em", color: "white" }}>WorkiTalk</span>
                    </Link>

                    {/* 네비게이션 */}
                    <nav style={{ display: "flex", gap: 2, flex: 1 }}>
                        {isAuthenticated && (
                            <>
                                {isAdmin ? (
                                    <>
                                        <NavDropdown label="조직" isActiveCheck={isActive('Appr')} menuName="org">
                                            <DropItem to="/admin/pendingAppr" isActive={isActive('/admin/pendingAppr')}>조직 관리</DropItem>
                                        </NavDropdown>
                                        <NavDropdown label="회원" isActiveCheck={isActive('readUser')} menuName="member">
                                            <DropItem to="/admin/readUserList" isActive={isActive('/admin/readUserList')}>회원 관리</DropItem>
                                        </NavDropdown>
                                        <NavDropdown label="푸시" isActiveCheck={isActive('push')} menuName="push">
                                            <DropItem to="/admin/push/list" reloadDocument isActive={isActive('/admin/push/list')}>푸시 목록</DropItem>
                                            <DropItem to="/admin/push/create" reloadDocument isActive={isActive('/admin/push/create')}>푸시 생성</DropItem>
                                            <DropItem to="/admin/push/template/list" reloadDocument isActive={isActive('/admin/push/template/list')}>템플릿 목록</DropItem>
                                            <DropItem to="/admin/push/template/create" reloadDocument isActive={isActive('/admin/push/template/create')}>템플릿 생성</DropItem>
                                        </NavDropdown>
                                        <NavDropdown label="설정" isActiveCheck={isActive('/admin/settings')} menuName="settings">
                                            <DropItem to="/admin/settings" reloadDocument isActive={isActive('/admin/settings')}>어드민 설정</DropItem>
                                        </NavDropdown>
                                    </>
                                ) : (
                                    <>
                                        {/* 조직 */}
                                        <NavDropdown label="조직" isActiveCheck={isActive('organization')} menuName="org">
                                            {/* 권한 생성이전까지는 일단 오픈 */}
                                            <DropItem to="/organization/info" isActive={isActive('/organization/info')}>조직 정보</DropItem>
                                            {/* 회원 목록은 조직정보 화면의 탭(OrgSectionTabs)으로 이동 — 헤더 중복 제거 */}
                                            <DropItem to="/my-info/check-pw" isActive={isActive('my-info')}>내 정보 관리</DropItem>
                                            {/* <DropItem to="/organization/orgAdd" isActive={isActive('/organization/orgAdd')}>조직 생성</DropItem>
                                            <DropItem to="/organization/join" isActive={isActive('/organization/join')}>조직 가입</DropItem> */}
                                        </NavDropdown>

                                        {/* 회원 */}
                                        {/* {user.currentOrganizationApproved && (
                                            <NavDropdown label="회원" isActiveCheck={isActive('employee')} menuName="member">
                                                <DropItem to="/employee/list" reloadDocument isActive={isActive('/employee/list')}>회원 목록</DropItem>
                                            </NavDropdown>
                                        )} */}


                                        {/* 전자결재 */}
                                        {user.currentOrganizationApproved && (
                                            <NavDropdown label="전자결재" isActiveCheck={isActive('approval')} menuName="approval">
                                                <DropItem to="/approval/write" isActive={isActive('/approval/write')}>기안 작성</DropItem>
                                                <DropItem to="/approval/draft-box" isActive={isActive('/approval/draft-box')}>기안함</DropItem>
                                                <DropItem to="/approval/inbox" isActive={isActive('/approval/inbox')}>결재함</DropItem>
                                            </NavDropdown>
                                        )}

                                        {/* 일정 */}
                                        {user.currentOrganizationApproved && (
                                            <div style={{ position: "relative" }}>
                                                <Link to="/calendar" reloadDocument style={{
                                                    ...navBtnStyle(isActive('/calendar')),
                                                    textDecoration: "none",
                                                    display: "inline-flex", alignItems: "center",
                                                }}>
                                                    일정
                                                    {isActive('/calendar') && <ActiveBar/>}
                                                </Link>
                                            </div>
                                        )}

                                        {/* 프로젝트 */}
                                        {user.currentOrganizationApproved && (
                                            <div style={{ position: "relative" }}>
                                                <Link to="/project/list" reloadDocument style={{
                                                    ...navBtnStyle(isActive('project')),
                                                    textDecoration: "none",
                                                    display: "inline-flex", alignItems: "center",
                                                }}>
                                                    프로젝트
                                                    {isActive('project') && <ActiveBar/>}
                                                </Link>
                                            </div>
                                        )}

                                        {/* 보고서 */}
                                        {user.currentOrganizationApproved && (
                                            <NavDropdown label="보고서" isActiveCheck={isActive('report')} menuName="report">
                                                <DropItem to="/report/create" isActive={isActive('/report/create')}>보고서 작성</DropItem>
                                                <DropItem to="/report" isActive={location.pathname === '/report'}>보고서 목록</DropItem>
                                            </NavDropdown>
                                        )}


                                        {/* 푸시 */}
                                        {user.currentOrganizationApproved && (
                                            <NavDropdown label="푸시" isActiveCheck={isActive('push')} menuName="push">
                                                <DropItem to="/push/list" reloadDocument isActive={isActive('/push/list')}>푸시 목록</DropItem>
                                                <DropItem to="/push/create" reloadDocument isActive={isActive('/push/create')}>푸시 생성</DropItem>
                                                <DropItem to="/push/template/list" reloadDocument isActive={isActive('/push/template/list')}>템플릿 목록</DropItem>
                                                <DropItem to="/push/template/create" reloadDocument isActive={isActive('/push/template/create')}>템플릿 생성</DropItem>
                                            </NavDropdown>
                                        )}

                                        {/* 노트 */}
                                        {user.currentOrganizationApproved && (
                                            <NavDropdown label="노트" isActiveCheck={isActive('note')} menuName="note">
                                                <DropItem to="/note/create" isActive={isActive('/note/create')}>새 노트 작성</DropItem>
                                                <DropItem to="/note" isActive={location.pathname === '/note'}>노트 목록</DropItem>
                                            </NavDropdown>
                                        )}
                                        
                                        {/* 마이페이지 */}
                                        {/* <div style={{ position: "relative" }}>
                                            <Link to="/my-info/check-pw" style={{
                                                ...navBtnStyle(isActive('my-info')),
                                                textDecoration: "none",
                                                display: "inline-flex", alignItems: "center",
                                            }}>
                                                마이페이지
                                                {isActive('my-info') && <ActiveBar/>}
                                            </Link>
                                        </div> */}
                                    </>
                                )}
                            </>
                        )}
                    </nav>

                    {/* 우측: 사용자 정보 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                        {isAuthenticated ? (
                            <>
                                <button style={{
                                    background: "transparent", border: `1px solid rgba(255,255,255,.15)`,
                                    width: 34, height: 34, borderRadius: 10,
                                    color: "rgba(255,255,255,.7)", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    position: "relative",
                                }}>
                                    <BellIcon width={16} height={16}/>
                                </button>

                                {!isAdmin && (
                                    <div
                                        style={{ position: "relative" }}
                                        onMouseEnter={() => handleMouseEnter('orgList')}
                                        onMouseLeave={handleMouseLeave}
                                    >
                                        <button style={{
                                            background: "transparent",
                                            border: "1px solid rgba(255,255,255,.15)",
                                            borderRadius: 8, padding: "5px 10px",
                                            color: "rgba(255,255,255,.75)",
                                            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
                                            display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                                        }}>
                                            {user?.currentOrganizationName}
                                            <span style={{ fontSize: 11, color: "rgba(255,255,255,.45)" }}>
                                                {user?.currentRole}
                                            </span>
                                            <ChevronIcon width={13} height={13} style={{ color: "rgba(255,255,255,.45)" }}/>
                                        </button>
                                        {activeDropdown === 'orgList' && (
                                            <div style={{ ...dropdownPanel, left: "auto", right: 0, minWidth: 220 }}>
                                                {(user?.orgUserList ?? []).map((org) => (
                                                    <div
                                                        key={org.organizationCode}
                                                        style={{ ...dropdownItemBase, display: "flex", alignItems: "center", justifyContent: "space-between" }}
                                                        onClick={() => handleOrgSwitch(org)}
                                                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f4fa"; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                                    >
                                                        <span>{org.organizationName} <span style={{ fontSize: 11, color: "#8b8a99" }}>{org.userRole}</span></span>
                                                        <span style={{ display: "flex", gap: 4 }}>
                                                            {org.organizationCode === user.currentOrganizationCode && (
                                                                <span style={{ fontSize: 10, background: hexToRgba(MINT, 0.15), color: MINT, padding: "2px 6px", borderRadius: 999, fontWeight: 600 }}>현재</span>
                                                            )}
                                                            {org.default && (
                                                                <span style={{ fontSize: 10, background: hexToRgba(PURPLE, 0.12), color: PURPLE, padding: "2px 6px", borderRadius: 999, fontWeight: 600 }}>기본</span>
                                                            )}
                                                            {!org.isApproved && (
                                                                <span style={{ fontSize: 10, background: "#fde8e8", color: "#e05252", padding: "2px 6px", borderRadius: 999, fontWeight: 600 }}>미승인</span>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                                <div style={{ height: 1, background: "#f0eff5", margin: "4px 0" }}/>
                                                <div style={{ padding: "6px 10px" }}>
                                                    <button
                                                        onClick={() => openOrgSelectModal()}
                                                        style={{
                                                            width: "100%", padding: "7px 12px",
                                                            border: `1px solid ${PURPLE}`,
                                                            borderRadius: 8, background: "transparent",
                                                            color: PURPLE, fontSize: 12, fontWeight: 600,
                                                            cursor: "pointer", fontFamily: "inherit",
                                                        }}
                                                    >
                                                        기본 조직 설정
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 10, borderLeft: "1px solid rgba(255,255,255,.12)" }}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: "50%",
                                        background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 12, fontWeight: 700, color: "white", flexShrink: 0,
                                    }}>
                                        {(user?.userName || '').slice(0, 1)}
                                    </div>
                                    <NavDropdown
                                        label={
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap"
                                                }}
                                            >
                                                {user?.userName}님
                                            </span>
                                        }
                                        isActiveCheck={isActive('my-init-info')}
                                        menuName="init-info"
                                    >
                                        <DropItem
                                            to="/my-info/check-pw"
                                            isActive={isActive('/my-info/check-pw')}
                                        >
                                            내 기본 정보 관리
                                        </DropItem>
                                    </NavDropdown>
                                </div>

                                <button
                                    onClick={() => onLogout()}
                                    title="로그아웃"
                                    style={{
                                        background: "transparent", border: "none",
                                        padding: 6, cursor: "pointer",
                                        color: "rgba(255,255,255,.5)", display: "flex",
                                        borderRadius: 6,
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = "white"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,.5)"; }}
                                >
                                    <LogoutIcon width={16} height={16}/>
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/" style={{ fontSize: 13.5, color: "rgba(255,255,255,.7)", textDecoration: "none" }}>
                                    로그인
                                </Link>
                                <Link to="/register" style={{
                                    fontSize: 13.5, color: "white", textDecoration: "none",
                                    background: hexToRgba(PURPLE, 0.3),
                                    padding: "6px 14px", borderRadius: 8,
                                }}>
                                    회원 가입
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>
            {isOrgSelectModalOpen && <OrganizationSelectModal/>}
        </>
    );
}
